import {CheckIcon, Checkbox, Combobox, Modal, Pill, PillsInput, ScrollArea, useCombobox} from "@mantine/core";
import type {DatesRangeValue} from "@mantine/dates";
import {DateTime} from "luxon";
import React, {useContext, useEffect, useState} from "react";
import {z as zod} from "zod";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {useIntellsysConfiguration} from "~/contexts/intellsysConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useHeimdallUser} from "~/contexts/HeimdallUserContext";
import {twMerge} from "tailwind-merge";
import {DateRangePicker} from "~/components/reports/dateRangePicker";
import {
    AreaGraphWidgetComponent,
    BarGraphWidgetComponent,
    ComboChartWidgetComponent,
    DonutChartWidgetComponent,
    GaugeWidgetComponent,
    HorizontalBarGraphWidgetComponent,
    LineGraphWidgetComponent,
    NumberTileWidgetComponent,
    PieChartWidgetComponent,
    ProgressBarWidgetComponent,
    StackedBarGraphWidgetComponent,
    TableWidgetComponent,
    TextWidgetComponent,
} from "~/components/reports/widgetSpecific/widgetRenderers";
import {WidgetLoadingState, type Widget} from "~/components/reports/widgetSpecific/widgets";
import {DateRangeOptions, WidgetType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import {OutlineMultipleDropdown} from "~/components/reusableComponents/dropdowns";
import {ReportViewPopupContext} from "~/contexts/reportViewPopupContext";
import {ReportActionType} from "~/modules/reports/state/reportState";
import type {
    ReportAction,
    ReportState,
    WidgetDataAction,
} from "~/modules/reports/state/reportState";
import type {
    WidgetFrontendFilters,
    WidgetSorts,
} from "~/modules/reports/domain/reportTypes";
import {
    ColumnSortType,
    FilterOperations,
} from "~/modules/reports/domain/reportTypes";
import {
    getDataPointsFromWidgetForWidgetType,
    getDatesForDateRangeOption,
} from "~/modules/reports/utilities/reportUtilities";
import type {Iso8601DateTime} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {VerticalSpacer} from "@vesta/global-common-typescript/components/verticalSpacer";
import {distinct} from "@vesta/global-common-typescript/utilities/utilities";

const REPORT_FRONTEND_FILTER_DISTINCT_VALUE_LIMIT = 9999;

export type ViewWidgetComponentProps = {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    reportState: ReportState;
    reportDispatch: React.Dispatch<ReportAction>;
    widgetDataDispatch: React.Dispatch<WidgetDataAction>;
    pageIndex: number;
};

function commonOnCalendarDatesChangeFunction(values: DatesRangeValue, timezone: string, widget: Widget, reportDispatch: React.Dispatch<ReportAction>) {
    const [startRaw, endRaw] = values;

    let startJs: Date | null = null;
    let startIso: Iso8601DateTime | null = null;
    if (startRaw !== null) {
        startJs = new Date(startRaw);
        startIso = DateTime.fromJSDate(startJs).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
    }

    let endJs: Date | null = null;
    let endIso: Iso8601DateTime | null = null;
    if (endRaw !== null) {
        endJs = new Date(endRaw);
        endIso = DateTime.fromJSDate(endJs).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
    }
    reportDispatch({
        actionType: ReportActionType.editWidget,
        widget: widget,
        changedFields: {
            startDateJs: startJs,
            endDateJs: endJs,
        },
    });

    if (startJs !== null && endJs !== null) {
        reportDispatch({
            actionType: ReportActionType.editWidget,
            widget: widget,
            changedFields: {
                startDateIso: startIso,
                endDateIso: endIso,
                dateRangeCanvasControl: null,
                dateRange: null,
            },
        });
    }
}

export function MultiBarGraphViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportState,
    reportDispatch,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type !== WidgetType.multiBarGraph) {
        throw new Error("9352c22a-fdbd-4e55-a0ac-3b269f17456e");
    }

    return (
        <BarGraphWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            barGraphType="default"
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    canvasControls={reportState.canvasControls}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRangeCanvasControl === null ? widget.dateRange : widget.dateRangeCanvasControl}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
            frontendFilters={
                <FrontendFilters
                    reportDispatch={reportDispatch}
                    widget={widget}
                />
            }
        />
    );
}

export function StackedBarGraphViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportState,
    reportDispatch,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type != WidgetType.stackedBarGraph) {
        throw new Error("070f8f2b-d0bd-4bfb-85fc-1d1497d8634e");
    }

    return (
        <StackedBarGraphWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            barGraphType="stacked"
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    canvasControls={reportState.canvasControls}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRangeCanvasControl === null ? widget.dateRange : widget.dateRangeCanvasControl}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
            frontendFilters={
                <FrontendFilters
                    reportDispatch={reportDispatch}
                    widget={widget}
                />
            }
        />
    );
}

export function HorizontalBarGraphViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportState,
    reportDispatch,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type !== WidgetType.horizontalBarGraph) {
        throw new Error("12e09f12-09a8-4e17-8361-f7b1331f8a29");
    }

    return (
        <HorizontalBarGraphWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            barGraphType="default"
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    canvasControls={reportState.canvasControls}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRangeCanvasControl === null ? widget.dateRange : widget.dateRangeCanvasControl}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
            frontendFilters={
                <FrontendFilters
                    reportDispatch={reportDispatch}
                    widget={widget}
                />
            }
        />
    );
}

export function AreaGraphViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type !== WidgetType.areaGraph) {
        throw new Error("09b88d75-6a7a-47c4-9703-cb503884f474");
    }

    return (
        <AreaGraphWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    canvasControls={reportState.canvasControls}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRangeCanvasControl === null ? widget.dateRange : widget.dateRangeCanvasControl}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
            frontendFilters={
                <FrontendFilters
                    reportDispatch={reportDispatch}
                    widget={widget}
                />
            }
        />
    );
}

export function ComboChartViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type !== WidgetType.comboChart) {
        throw new Error("91577858-294a-4c42-bec8-38a701b7ab89");
    }

    return (
        <ComboChartWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    canvasControls={reportState.canvasControls}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRangeCanvasControl === null ? widget.dateRange : widget.dateRangeCanvasControl}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
            frontendFilters={
                <FrontendFilters
                    reportDispatch={reportDispatch}
                    widget={widget}
                />
            }
        />
    );
}

export function LineGraphViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type != WidgetType.lineGraph) {
        throw new Error("7a833357-25e2-4e38-9329-fdeddc22fbdf");
    }

    return (
        <LineGraphWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    canvasControls={reportState.canvasControls}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRangeCanvasControl === null ? widget.dateRange : widget.dateRangeCanvasControl}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
            frontendFilters={
                <FrontendFilters
                    reportDispatch={reportDispatch}
                    widget={widget}
                />
            }
        />
    );
}

export function NumberTileViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type != WidgetType.numberTile) {
        throw new Error("984cd812-6ead-4f84-a64b-97d7400fd8a3");
    }

    return (
        <NumberTileWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    canvasControls={reportState.canvasControls}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRangeCanvasControl === null ? widget.dateRange : widget.dateRangeCanvasControl}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
        />
    );
}

export function PieChartViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type != WidgetType.pieChart) {
        throw new Error("db9f848d-1165-49e8-b1ba-fd7975f70832");
    }

    return (
        <PieChartWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    canvasControls={reportState.canvasControls}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRangeCanvasControl === null ? widget.dateRange : widget.dateRangeCanvasControl}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
            frontendFilters={
                <FrontendFilters
                    reportDispatch={reportDispatch}
                    widget={widget}
                />
            }
        />
    );
}

export function DonutChartViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type != WidgetType.donutChart) {
        throw new Error("9dd78927-e516-460f-8cf7-af3ba9410d12");
    }

    return (
        <DonutChartWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    canvasControls={reportState.canvasControls}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRangeCanvasControl === null ? widget.dateRange : widget.dateRangeCanvasControl}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
            frontendFilters={
                <FrontendFilters
                    reportDispatch={reportDispatch}
                    widget={widget}
                />
            }
        />
    );
}

export function TextViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type != WidgetType.text) {
        throw new Error("c000dc32-b635-4198-8213-adba578d0503");
    }

    return (
        <TextWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
        />
    );
}

export function ImageViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor: number,
    reportDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type != WidgetType.image) {
        throw new Error("a899fbaf-96ab-48e0-a237-e5ac14de4dee");
    }

    return <div className={twMerge("p-4", className)}>Image</div>;
}

export function TableViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportDispatch,
    widgetDataDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type != WidgetType.table) {
        throw new Error("4cf26b16-4d96-4e80-8f4f-d03f36d33abc");
    }

    const invalidateWidgetData = () => {
        widgetDataDispatch({
            type: "invalidateDataForWidget",
            widgetId: widget.id,
        });
    };

    const [pendingComparisonDates, setPendingComparisonDates] = useState<DatesRangeValue | null>(null);

    const updateComparisonDateRange = (startDate: Iso8601DateTime, endDate: Iso8601DateTime) => {
        reportDispatch({
            actionType: ReportActionType.editWidget,
            widget: widget,
            changedFields: {
                comparison: {
                    ...widget.comparison,
                    enabled: true,
                    startDateIso: startDate,
                    endDateIso: endDate,
                },
                comparisonData: null,
                comparisonColumnMetadata: null,
                comparisonColumnAttributeIds: null,
                comparisonLoadingState: WidgetLoadingState.notStarted,
                comparisonErrorCode: null,
            },
        });
        setPendingComparisonDates(null);
        invalidateWidgetData();
    };

    const comparisonDatePicker = widget.comparison.enabled ? (
        <DateRangePicker
            isDateRangeEditable={true}
            timezone={reportState.timezone}
            targetClassName="mx-0"
            onCloseFunction={() => setPendingComparisonDates(null)}
            values={pendingComparisonDates ?? [
                widget.comparison.startDateIso === null ? null : DateTime.fromISO(widget.comparison.startDateIso).setZone(reportState.timezone).toJSDate(),
                widget.comparison.endDateIso === null ? null : DateTime.fromISO(widget.comparison.endDateIso).setZone(reportState.timezone).toJSDate(),
            ]}
            onSubOptionClickFunction={(option) => {
                if (Object.values(DateRangeOptions).includes(option as DateRangeOptions) === false) {
                    return;
                }

                const dates = getDatesForDateRangeOption(option as DateRangeOptions, reportState.timezone);
                updateComparisonDateRange(dates.startDate, dates.endDate);
            }}
            canvasControls={null}
            existingDateRangeOption={null}
            onCalendarDatesChangeFunction={(values) => {
                const [startRaw, endRaw] = values;
                if (startRaw === null || endRaw === null) {
                    setPendingComparisonDates([startRaw, endRaw]);
                    return;
                }

                const startDate = DateTime.fromJSDate(new Date(startRaw)).setZone(reportState.timezone).toUTC().toISO() as Iso8601DateTime;
                const endDate = DateTime.fromJSDate(new Date(endRaw)).setZone(reportState.timezone).toUTC().toISO() as Iso8601DateTime;
                updateComparisonDateRange(startDate, endDate);
            }}
        />
    ) : null;

    return (
        <TableWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={
                        widget.comparison.enabled === true ||
                        (reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null)
                    }
                    timezone={reportState.timezone}
                    targetClassName={widget.comparison.enabled ? "mx-0" : undefined}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                        invalidateWidgetData();
                    }}
                    canvasControls={reportState.canvasControls}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRangeCanvasControl === null ? widget.dateRange : widget.dateRangeCanvasControl}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                        if (values[0] !== null && values[1] !== null) {
                            invalidateWidgetData();
                        }
                    }}
                />
            }
            comparisonDatePicker={comparisonDatePicker}
            comparisonDateRangePending={pendingComparisonDates !== null}
            onSortSelect={(key) => {
                const currentSorts = widget.columnsToSort ?? {};

                if (currentSorts[key] == null) {
                    const newSorts: WidgetSorts = {};

                    for (const [sortKey] of Object.entries(currentSorts)) {
                        newSorts[sortKey] = null;
                    }

                    newSorts[key] = ColumnSortType.descending;

                    reportDispatch({
                        actionType: ReportActionType.editWidget,
                        widget: widget,
                        changedFields: {
                            columnsToSort: newSorts,
                        },
                    });
                    return;
                }

                if (currentSorts[key] === ColumnSortType.descending) {
                    const newSorts: WidgetSorts = {};

                    for (const [sortKey] of Object.entries(currentSorts)) {
                        newSorts[sortKey] = null;
                    }

                    newSorts[key] = ColumnSortType.ascending;

                    reportDispatch({
                        actionType: ReportActionType.editWidget,
                        widget: widget,
                        changedFields: {
                            columnsToSort: newSorts,
                        },
                    });
                    return;
                }

                if (currentSorts[key] === ColumnSortType.ascending) {
                    const newSorts: WidgetSorts = {};

                    for (const [sortKey] of Object.entries(currentSorts)) {
                        newSorts[sortKey] = null;
                    }

                    reportDispatch({
                        actionType: ReportActionType.editWidget,
                        widget: widget,
                        changedFields: {
                            columnsToSort: newSorts,
                        },
                    });
                    return;
                }
            }}
            frontendFilters={
                <FrontendFilters
                    reportDispatch={reportDispatch}
                    widget={widget}
                />
            }
        />
    );
}

export function ProgressBarViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type != WidgetType.progressBar) {
        throw new Error("7426ea3b-d6fa-4884-b6fd-144f53ce9f6a");
    }

    return (
        <ProgressBarWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRange}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
        />
    );
}

export function GaugeViewWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    reportDispatch,
    reportState,
    pageIndex,
}: ViewWidgetComponentProps) {
    if (widget.type != WidgetType.gauge) {
        throw new Error("349f7024-2576-49e8-bff0-281548329eb7");
    }

    return (
        <GaugeWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            datePicker={
                <DateRangePicker
                    isDateRangeEditable={reportState.canvasControls != null && reportState.canvasControls[widget.dateRange] != null}
                    timezone={reportState.timezone}
                    values={[widget.startDateJs, widget.endDateJs]}
                    onSubOptionClickFunction={(option) => {
                        reportDispatch({
                            actionType: ReportActionType.editWidget,
                            widget: widget,
                            changedFields: {
                                dateRange: option,
                            },
                        });
                    }}
                    pageStartDate={reportState.pages[pageIndex]?.startDateIso}
                    pageEndDate={reportState.pages[pageIndex]?.endDateIso}
                    existingDateRangeOption={widget.dateRange}
                    onCalendarDatesChangeFunction={(values) => {
                        commonOnCalendarDatesChangeFunction(values, reportState.timezone, widget, reportDispatch);
                    }}
                />
            }
        />
    );
}

export function FilterPopup({reportState, reportDispatch}: {reportState: ReportState; reportDispatch: React.Dispatch<ReportAction>}) {
    const widget = reportState.selectedWidget;

    const popupContext = useContext(ReportViewPopupContext);

    if (widget == null) {
        return;
    }
    const data = getDataPointsFromWidgetForWidgetType(widget);

    if (data == null || widget.filters == null) {
        return;
    }

    return (
        <Modal
            opened={popupContext.isPopupOpened}
            onClose={() => {
                reportDispatch({
                    actionType: ReportActionType.setSelectedWidget,
                    widget: null,
                });
                popupContext.close();
            }}
            centered
            classNames={{
                content: "!bg-ds-surface pt-2 pb-8 rounded-xl",
                header: "!bg-transparent !p-0 !h-16",
                body: "!p-0 !overflow-auto",
                title: "!font-semibold !text-[1.125rem] !pl-6",
                close: "!mr-6",
            }}
            right={"16px"}
            title="Filters"
        >
            <div className="w-full h-px bg-sg-ts-delayed-stroke-primary" />

            <VerticalSpacer className="h-4" />

            <div className="max-h-[15rem] overflow-y-auto overflow-x-clip px-6">
                {Object.keys(widget.filters).some((key) => Object.keys(data[0]).includes(key)) && (
                    <ItemBuilder
                        items={Object.keys(data[0])}
                        itemBuilder={(key, keyIndex) => {
                            return (
                                Object.keys(widget.filters).includes(key) && (
                                    <React.Fragment key={`${keyIndex}${key}`}>
                                        <div className="pb-2">{key}</div>
                                        <OutlineMultipleDropdown
                                            searchable={true}
                                            items={distinct(data.map((dataPoint) => `${dataPoint[key]}`))}
                                            selectedItems={widget.filters[key][FilterOperations.equals]}
                                            setSelectedItems={(items) => {
                                                reportDispatch({
                                                    actionType: ReportActionType.editSelectedWidget,
                                                    widget: widget,
                                                    changedFields: {
                                                        filters: {
                                                            ...widget.filters,
                                                            [key]: {
                                                                [FilterOperations.equals]: items,
                                                            },
                                                        },
                                                    },
                                                });
                                            }}
                                            rootClassName="w-full"
                                            styles={{
                                                pill: {
                                                    display: "none",
                                                },
                                            }}
                                            placeholder={`${widget.filters[key][FilterOperations.equals].length} ${
                                                widget.filters[key][FilterOperations.equals].length === 1 ? "value" : "values"
                                            } selected`}
                                        />

                                        <VerticalSpacer className="h-4" />
                                    </React.Fragment>
                                )
                            );
                        }}
                    />
                )}
            </div>
        </Modal>
    );
}

type FilterableWidget = Widget & {filters: WidgetFrontendFilters; datasetId: string | null};

export function FrontendFilters({reportDispatch, widget}: {reportDispatch: React.Dispatch<ReportAction>; widget: Widget | null}) {
    const {intellsysBaseUrl} = useIntellsysConfiguration();
    const {accessToken} = useHeimdallUser();
    const {workspace} = useWorkspace();
    const workspaceId = workspace.id;
    const [fetchedOptions, setFetchedOptions] = useState<Record<string, Array<string>>>({});
    const [isFetchingOptions, setIsFetchingOptions] = useState<Record<string, boolean>>({});

    const fw = widget as FilterableWidget | null;
    const filterKeys = fw?.filters != null ? Object.keys(fw.filters) : [];

    useEffect(() => {
        if (fw == null || fw.filters == null || fw.datasetId == null) {
            return;
        }
        for (const key of Object.keys(fw.filters)) {
            const filterEntry = fw.filters[key];
            if (filterEntry === undefined || !filterEntry.attributeId || fetchedOptions[key] !== undefined) {
                continue;
            }
            fetchOptionsForKey(key, filterEntry.attributeId);
        }
    }, [filterKeys.join(","), fw?.datasetId]);

    if (widget == null || widget.filters == null) {
        return null;
    }

    async function fetchOptionsForKey(key: string, attributeId: string) {
        if (widget == null || widget.datasetId == null) {
            return;
        }
        setIsFetchingOptions((prev) => ({...prev, [key]: true}));
        const result = await robustJsonResultClientFetch(
            `${intellsysBaseUrl}/sub-system/erato/analytics/dataset/distinct-values`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    datasetId: widget.datasetId,
                    workspaceId,
                    attributeId,
                    limit: REPORT_FRONTEND_FILTER_DISTINCT_VALUE_LIMIT,
                }),
            },
            zod.array(zod.union([zod.string(), zod.number(), zod.boolean(), zod.null()])),
        );
        setIsFetchingOptions((prev) => ({...prev, [key]: false}));
        if (result.success === true) {
            setFetchedOptions((prev) => ({
                ...prev,
                [key]: result.data
                    .filter((value) => value !== null)
                    .map(String),
            }));
        }
    }

    const keys = Object.keys(widget.filters);
    if (keys.length === 0) {
        return null;
    }

    return (
        <div className="max-h-[22.5rem] overflow-y-auto overflow-x-clip grid grid-flow-row auto-rows-max items-center gap-x-2 gap-y-4 w-80 px-4 pb-5 pt-4 bg-ds-surface-raised">
            <div className="gj-text-subtitle-400 !font-normal text-ds-text">Apply Filters</div>

            <ItemBuilder
                items={keys}
                itemBuilder={(key, keyIndex) => {
                    const options = fetchedOptions[key] ?? [];
                    return (
                        <div
                            key={`${keyIndex}${key}`}
                            className="grid grid-flow-row gap-y-2 auto-rows-max"
                        >
                            <div
                                className="col-start-1 text-left gj-text-label-500 text-ds-text-subtle text-ellipsis max-w-full whitespace-nowrap overflow-hidden"
                                title={key}
                            >
                                {isFetchingOptions[key] === true ? `${key} (loading...)` : key}
                            </div>
                            <FilterValueSelectCombobox
                                optionValues={options}
                                onOptionSubmit={(item) => {
                                    reportDispatch({
                                        actionType: ReportActionType.editWidget,
                                        widget: widget,
                                        changedFields: {
                                            filters: {
                                                ...widget.filters,
                                                [key]: {
                                                    ...widget.filters[key],
                                                    [FilterOperations.equals]: [...widget.filters[key][FilterOperations.equals], item],
                                                },
                                            },
                                        },
                                    });
                                }}
                                isOptionSelected={(item) => widget.filters[key][FilterOperations.equals].includes(item)}
                                onOptionRemove={(item) => {
                                    reportDispatch({
                                        actionType: ReportActionType.editWidget,
                                        widget: widget,
                                        changedFields: {
                                            filters: {
                                                ...widget.filters,
                                                [key]: {
                                                    ...widget.filters[key],
                                                    [FilterOperations.equals]: widget.filters[key][FilterOperations.equals].filter((value) => value != item),
                                                },
                                            },
                                        },
                                    });
                                }}
                                selectedValues={widget.filters[key][FilterOperations.equals]}
                                selectAll={(selected) => {
                                    reportDispatch({
                                        actionType: ReportActionType.editWidget,
                                        widget: widget,
                                        changedFields: {
                                            filters: {
                                                ...widget.filters,
                                                [key]: {
                                                    ...widget.filters[key],
                                                    [FilterOperations.equals]: selected === true ? distinct(options) : [],
                                                },
                                            },
                                        },
                                    });
                                }}
                            />
                        </div>
                    );
                }}
            />
        </div>
    );
}

export function FilterValueSelectCombobox({
    onOptionSubmit,
    optionValues,
    isOptionSelected,
    onOptionRemove,
    selectedValues,
    selectAll,
}: {
    onOptionSubmit: (value: string) => void;
    isOptionSelected: (value: string) => boolean;
    optionValues: Array<string>;
    onOptionRemove: (value: string) => void;
    selectedValues: Array<string>;
    selectAll: (selected: boolean) => void;
}) {
    // const [opened, setOpened] = useState(false);
    const combobox = useCombobox({});

    const [searchQuery, setSearchQuery] = useState("");

    const options = optionValues
        .filter((item) => item.toLowerCase().includes(searchQuery.trim().toLowerCase()))
        .map((item) => (
            <Combobox.Option
                value={item}
                key={item}
                active={selectedValues.includes(item)}
            >
                <div className="grid grid-flow-col grid-cols-[minmax(0,1fr)_max-content] items-center gap-x-4">
                    <span className="text-ellipsis overflow-hidden max-w-full whitespace-nowrap">{item}</span>
                    {selectedValues.includes(item) ? (
                        <CheckIcon
                            size={12}
                            className="text-ds-icon fill-ds-icon"
                        />
                    ) : null}
                </div>
            </Combobox.Option>
        ));

    const maxDisplayedValues = 2;

    const values = selectedValues.slice(0, maxDisplayedValues === selectedValues.length ? maxDisplayedValues : maxDisplayedValues - 1).map((item) => (
        <Pill
            key={item}
            withRemoveButton
            onRemove={() => onOptionRemove(item)}
        >
            {item}
        </Pill>
    ));

    return (
        <Combobox
            store={combobox}
            transitionProps={{transition: "pop", duration: 200}}
            onOptionSubmit={(item) => {
                if (selectedValues.includes(item)) {
                    onOptionRemove(item);
                } else {
                    onOptionSubmit(item);
                }
            }}
            withinPortal={false}
        >
            <Combobox.DropdownTarget>
                <PillsInput
                    onClick={() => {
                        // setOpened(true);
                        combobox.openDropdown();
                    }}
                    classNames={{
                        input: "!bg-ds-surface !border !border-ds-border",
                    }}
                >
                    <Pill.Group>
                        {selectedValues.length > 0 ? (
                            <>
                                {values}
                                {selectedValues.length > maxDisplayedValues && <Pill>+{selectedValues.length - (maxDisplayedValues - 1)} more</Pill>}
                            </>
                        ) : (
                            <></>
                        )}

                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                onFocus={() => combobox.openDropdown()}
                                onBlur={() => combobox.closeDropdown()}
                                value={searchQuery}
                                placeholder="Search values"
                                onChange={(event) => {
                                    combobox.updateSelectedOptionIndex();
                                    setSearchQuery(event.currentTarget.value);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Backspace" && searchQuery.length === 0) {
                                        event.preventDefault();
                                        onOptionRemove(selectedValues[selectedValues.length - 1]);
                                    }
                                }}
                            />
                        </Combobox.EventsTarget>
                    </Pill.Group>
                </PillsInput>
            </Combobox.DropdownTarget>

            <Combobox.Dropdown
                classNames={{
                    dropdown: "!bg-ds-surface !overflow-y-hidden",
                }}
            >
                <Combobox.Options
                    mah={400}
                    style={{overflow: "hidden"}}
                >
                    <ScrollArea.Autosize
                        mah={200}
                        type="scroll"
                    >
                        {options.length > 0 ? options : <Combobox.Empty>Nothing found...</Combobox.Empty>}
                    </ScrollArea.Autosize>
                    <Combobox.Footer>
                        <div className="grid grid-flow-col auto-cols-max gap-x-2">
                            <Checkbox
                                onChange={(e) => {
                                    selectAll(e.target.checked);
                                }}
                            />
                            <div>Select All</div>
                        </div>
                    </Combobox.Footer>
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
}
